/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ThemeVariablesRoot
 *
 * @file ThemeVariablesRoot.jsx
 * @author Anuja Gaikwad
 * @description Lightweight wrapper that applies a root class which exposes
 *              CSS variables. Only the light theme variables are set; dark
 *              theme relies on existing fallbacks in component styles.
 */

import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';

const cssVarEntries = Object.entries(mixins).filter(
  ([key, value]) => typeof value === 'string'
    && !key.endsWith('-min')
    && !key.endsWith('-max')
    && !key.includes('-media')
    && !key.includes('-maxwidth')
    && !key.includes('-')
);

const cssVariables = cssVarEntries.reduce((acc, [key, value]) => {
  acc[`--${key}`] = value;

  return acc;
}, {});

const lightThemeVariables = {
  '--bg_quaternary': mixins.bg_quaternary,
  '--color_footer_bg': mixins.colorFooterBg,
  '--lightBgColor': mixins.lightBgColor,
  '--color_text_primary': mixins.color_text_primary,
  '--lightInactiveBgColor': mixins.lightInactiveBgColor,
  '--transparentBgColor': 'transparent',
  '--fontFamily': mixins.fontFamilyPlusSansPrimary,
  '--fontWeight400': mixins.fontWeight400,
  '--fontWeight500': mixins.fontWeight500,
  '--skeletonColor': mixins.skeletonColor,
  '--fontSize32': mixins.fontSize32,
  '--lineHeight40': '40px',
  '--letterSpacing64': '-0.64px',
  '--fontSize14': '14px',
  '--fontSize18': mixins.fontSize18,
  '--fontSize20': mixins.fontSize20,
  '--fontSize24': '24px',
  '--lineHeight16': '16px',
  '--lineHeight22': '22px',
  '--lineHeight28': '28px',
  '--lineHeight30': '30px',
  '--letterSpacing0': '0px',
  '--letterSpacing4': '-0.4px',
  '--letterSpacing48': '-0.48px',
  '--grayScaleTextSecondary': mixins.grayScaleTextSecondary,
  '--purpleSecondaryText': mixins.purpleSecondaryText,
  '--lightBgColor1': mixins.lightBgColor1,
  '--secondaryButtonFocusColor': mixins.secondaryButtonFocusColor
};

const ThemedRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLightTheme'
})(({ isLightTheme }) => ({
  '&&': {
    ...cssVariables,
    ...(isLightTheme ? lightThemeVariables : {})
  }
}));

const ThemeVariablesRoot = ({ theme, className, children, ...rest }) => {
  const isLightTheme = theme === 'light';

  return (
    <ThemedRoot
      isLightTheme={isLightTheme}
      className={isLightTheme ? `theme-light ${className || ''}`.trim() : (className || '')}
      {...rest}
    >
      {children}
    </ThemedRoot>
  );
};

ThemeVariablesRoot.propTypes = {
  theme: PropTypes.oneOf(['light', 'dark']),
  className: PropTypes.string,
  children: PropTypes.node
};

ThemeVariablesRoot.defaultProps = {
  theme: undefined,
  className: '',
  children: null
};

export default ThemeVariablesRoot;
